import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { Coins, Gift, LogOut, Copy, Sparkles, ArrowUpRight, ArrowDownLeft, TrendingUp, ShieldCheck, X, RotateCcw, Route, Lock } from "lucide-react";
import { useMember } from "@/contexts/MemberContext";
import { memberApi, formatMemberError } from "@/lib/memberClient";
import { BRAND } from "@/constants/brand";
import LoyaltyTrail from "@/components/LoyaltyTrail";

export default function MemberDashboard() {
  const { member, loading, logout, setMember } = useMember();
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [tab, setTab] = useState("overview"); // overview | rewards | loyalty | history
  const [redemptionModal, setRedemptionModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !member) nav("/member/login", { replace: true });
  }, [member, loading, nav]);

  const load = async () => {
    try {
      const [w, r, rd, ly] = await Promise.all([
        memberApi.get("/member/wallet"),
        memberApi.get("/member/rewards"),
        memberApi.get("/member/redemptions"),
        memberApi.get("/member/loyalty").catch(() => ({ data: null })),
      ]);
      setWallet(w.data);
      setRewards(r.data || []);
      setRedemptions(rd.data || []);
      setLoyalty(ly.data);
      if (setMember && w.data?.balance !== undefined) setMember((m) => (m ? { ...m, pop_coins: w.data.balance } : m));
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (member) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  const doLogout = () => { logout(); nav("/", { replace: true }); };

  const copyCode = () => {
    navigator.clipboard.writeText(member?.member_code || "");
    toast.success("Member ID copied");
  };

  const redeemReward = async (r) => {
    if ((wallet.balance || 0) < r.cost_coins) {
      toast.error(`You need ${r.cost_coins - wallet.balance} more Pop Coins`);
      return;
    }
    if (!window.confirm(`Redeem "${r.title}" for ${r.cost_coins} Pop Coins?`)) return;
    setBusy(true);
    try {
      const { data } = await memberApi.post("/member/rewards/redeem", { reward_id: r.id });
      setRedemptionModal({ code: data.code, reward: r });
      // Fire confetti
      confetti({ particleCount: 140, spread: 90, origin: { y: 0.6 }, colors: ["#FFC93C", "#FF4F5A", "#FFF8F0", "#2B2B2B"] });
      setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.4 }, colors: ["#FFC93C", "#FF4F5A"] }), 250);
      await load();
    } catch (e) {
      toast.error(formatMemberError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !member) {
    return <div className="min-h-screen grid place-items-center bg-[#2B2B2B] text-brand-cream">Loading your Panda pass…</div>;
  }

  return (
    <div data-testid="member-dashboard" className="min-h-screen bg-gradient-to-br from-[#2B2B2B] via-[#3a2f1a] to-[#1a1512] relative overflow-hidden">
      {/* animated blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full bg-brand-yellow/20 blur-3xl" animate={{ x: [0, 40, 0], y: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-1/2 right-0 w-[440px] h-[440px] rounded-full bg-[#FF4F5A]/15 blur-3xl" animate={{ x: [0, -30, 0], y: [0, 40, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6 text-brand-cream">
        <Link to="/" className="flex items-center gap-3" data-testid="member-nav-home">
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-9 w-9 bg-brand-cream rounded-full p-1.5" />
          <div className="font-display text-xl">Pop Panda Club</div>
        </Link>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-lg px-4 py-1.5 text-sm">
            <span className="text-brand-cream/60">Hey,</span>
            <span className="font-medium">{member.name?.split(" ")[0] || "Member"}</span>
          </div>
          <button onClick={doLogout} data-testid="member-logout" className="rounded-full bg-white/10 border border-white/15 backdrop-blur-lg px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-white/20 transition inline-flex items-center gap-1.5">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="relative z-10 px-4 sm:px-6 lg:px-12 pb-16">
        {/* Hero: Wallet + QR */}
        <section className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6 mt-4">
          {/* Wallet card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white/10 border border-white/15 backdrop-blur-2xl p-8 relative overflow-hidden shadow-2xl">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-brand-yellow/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-10 w-48 h-48 bg-[#FF4F5A]/15 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-yellow flex items-center gap-2">
                <Sparkles size={13} /> Pop Coin Wallet
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <motion.div
                  key={wallet.balance}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="font-display text-7xl text-brand-cream leading-none flex items-center gap-3"
                  data-testid="member-balance"
                >
                  <Coins className="text-brand-yellow" size={54} />
                  {wallet.balance ?? 0}
                </motion.div>
                <div className="text-brand-cream/60 text-sm">coins</div>
              </div>
              <div className="mt-3 text-brand-cream/70">
                <span className="capitalize">{member.tier || "bronze"}</span> tier · Member since {new Date(member.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => setTab("rewards")} data-testid="member-cta-rewards" className="rounded-full bg-brand-yellow text-brand-charcoal font-medium px-5 py-2.5 hover:brightness-95 transition inline-flex items-center gap-2">
                  <Gift size={16} /> Redeem Rewards
                </button>
                <Link to="/member/spin" data-testid="member-cta-spin" className="rounded-full bg-white/10 border border-white/20 backdrop-blur-lg px-5 py-2.5 hover:bg-white/20 transition inline-flex items-center gap-2 text-brand-cream">
                  <RotateCcw size={16} /> Spin & Win
                </Link>
                <Link to="/member/vault" data-testid="member-cta-vault" className="rounded-full bg-white/10 border border-white/20 backdrop-blur-lg px-5 py-2.5 hover:bg-white/20 transition inline-flex items-center gap-2 text-brand-cream">
                  <Lock size={16} /> Community Vault
                </Link>
                <button onClick={() => setTab("history")} data-testid="member-cta-history" className="rounded-full bg-white/10 border border-white/20 backdrop-blur-lg px-5 py-2.5 hover:bg-white/20 transition inline-flex items-center gap-2 text-brand-cream">
                  <TrendingUp size={16} /> View History
                </button>
              </div>
            </div>
          </motion.div>

          {/* QR card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-3xl bg-white/95 backdrop-blur-2xl p-6 shadow-2xl text-brand-charcoal flex flex-col items-center">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-charcoal/60">Your Membership QR</div>
            <motion.div
              whileHover={{ scale: 1.03, rotate: -1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="mt-4 rounded-2xl bg-white p-4 border border-brand-line shadow-sm"
              data-testid="member-qr"
            >
              <QRCodeSVG value={member.member_code || member.id} size={160} bgColor="#FFFFFF" fgColor="#2B2B2B" level="H" includeMargin={false} />
            </motion.div>
            <div className="mt-4 text-center">
              <div className="font-mono text-lg tracking-widest" data-testid="member-code">{member.member_code}</div>
              <button onClick={copyCode} data-testid="member-copy-code" className="mt-2 text-xs text-brand-charcoal/60 hover:text-brand-charcoal inline-flex items-center gap-1">
                <Copy size={12} /> Copy ID
              </button>
            </div>
            <div className="mt-3 text-xs text-brand-charcoal/55 text-center max-w-[240px] leading-snug">
              Show this at any Pop Panda outlet to earn coins or redeem rewards.
            </div>
          </motion.div>
        </section>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-white/10 overflow-x-auto">
          {[
            { key: "overview", label: "Overview" },
            { key: "rewards", label: "Rewards" },
            { key: "loyalty", label: "Loyalty" },
            { key: "payments", label: "Payments" },
            { key: "history", label: "History" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`member-tab-${t.key}`}
              className={`px-5 py-3 text-sm uppercase tracking-[0.2em] transition-colors border-b-2 whitespace-nowrap ${
                tab === t.key ? "border-brand-yellow text-brand-yellow" : "border-transparent text-brand-cream/60 hover:text-brand-cream"
              }`}
            >{t.label}</button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && <OverviewPane member={member} wallet={wallet} redemptions={redemptions} />}
          {tab === "rewards" && <RewardsPane rewards={rewards} balance={wallet.balance} onRedeem={redeemReward} busy={busy} />}
          {tab === "loyalty" && <LoyaltyPane loyalty={loyalty} />}
          {tab === "payments" && <PaymentsPane />}
          {tab === "history" && <HistoryPane transactions={wallet.transactions} redemptions={redemptions} />}
        </div>
      </main>

      {/* Redemption success modal */}
      <AnimatePresence>
        {redemptionModal && (
          <RedemptionModal data={redemptionModal} onClose={() => setRedemptionModal(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function OverviewPane({ member, wallet, redemptions }) {
  const recent = (wallet.transactions || []).slice(0, 5);
  const pending = (redemptions || []).filter((r) => r.status === "pending");
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <GlassCard>
        <SectionHead icon={ShieldCheck} label="Your Panda Profile" />
        <dl className="mt-4 space-y-3 text-sm">
          <Info k="Name" v={member.name} />
          <Info k="Email" v={member.email} />
          <Info k="Phone" v={member.phone} />
          <Info k="City" v={member.city || "—"} />
          <Info k="Member ID" v={member.member_code} mono />
          <Info k="Tier" v={<span className="capitalize">{member.tier || "bronze"}</span>} />
        </dl>
      </GlassCard>

      <GlassCard>
        <SectionHead icon={TrendingUp} label="Recent Activity" />
        {recent.length === 0 ? (
          <div className="mt-6 text-brand-cream/50 text-sm">No transactions yet. Your first coins are on the way!</div>
        ) : (
          <ul className="mt-4 space-y-3">
            {recent.map((t) => (
              <TxnRow key={t.id} t={t} />
            ))}
          </ul>
        )}
      </GlassCard>

      {pending.length > 0 && (
        <GlassCard className="lg:col-span-2">
          <SectionHead icon={Gift} label="Ready to Claim" />
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pending.map((r) => (
              <div key={r.id} data-testid={`pending-redemption-${r.code}`} className="rounded-2xl bg-brand-yellow/10 border border-brand-yellow/30 p-4">
                <div className="text-brand-cream font-medium">{r.reward_title}</div>
                <div className="mt-2 font-mono text-brand-yellow text-sm tracking-widest">{r.code}</div>
                <div className="mt-1 text-[11px] text-brand-cream/55">Show this code at the outlet</div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}

function RewardsPane({ rewards, balance, onRedeem, busy }) {
  if (!rewards.length) return <GlassCard><div className="text-brand-cream/60">No rewards available right now. Check back soon!</div></GlassCard>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {rewards.map((r, i) => {
        const affordable = balance >= r.cost_coins;
        return (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            whileHover={{ y: -6 }}
            className={`rounded-3xl border backdrop-blur-2xl p-6 relative overflow-hidden shadow-xl ${
              affordable ? "bg-white/10 border-white/20" : "bg-white/5 border-white/10 opacity-70"
            }`}
            data-testid={`reward-card-${r.id}`}
          >
            {r.image_url ? (
              <div className="h-32 -mx-6 -mt-6 mb-4 bg-brand-yellow/20 overflow-hidden">
                <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="h-32 -mx-6 -mt-6 mb-4 bg-gradient-to-br from-brand-yellow/40 to-[#FF4F5A]/30 flex items-center justify-center">
                <Gift size={44} className="text-brand-cream" />
              </div>
            )}
            <div className="text-[10px] uppercase tracking-[0.24em] text-brand-yellow">{r.category}</div>
            <div className="mt-1 font-display text-2xl text-brand-cream leading-tight">{r.title}</div>
            <div className="mt-2 text-sm text-brand-cream/65 min-h-[40px]">{r.description}</div>
            <div className="mt-4 flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-brand-yellow font-medium">
                <Coins size={16} /> {r.cost_coins}
              </div>
              <button
                disabled={!affordable || busy}
                onClick={() => onRedeem(r)}
                data-testid={`redeem-btn-${r.id}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  affordable ? "bg-brand-yellow text-brand-charcoal hover:brightness-95" : "bg-white/10 text-brand-cream/40 cursor-not-allowed"
                }`}
              >{affordable ? "Redeem" : `Need ${r.cost_coins - balance}`}</button>
            </div>
            {r.stock !== null && r.stock !== undefined && r.stock <= 10 && (
              <div className="mt-3 text-[11px] text-[#FF4F5A]">Only {r.stock} left</div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

function LoyaltyPane({ loyalty }) {
  if (!loyalty) return <GlassCard><div className="text-brand-cream/60">Loading loyalty journey…</div></GlassCard>;
  const { current, config, history = [], lifetime_earned = 0, lifetime_redeemed = 0 } = loyalty;
  const completed = current?.status === "completed";
  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
      <GlassCard>
        <SectionHead icon={Route} label={`Cycle ${current?.cycle_number || 1}`} />
        <div className="mt-3">
          <div className="text-brand-cream/75 text-sm">{completed ? "🎉 Your free popsicle is ready to claim!" : `${current?.progress || 0} of ${current?.target || config?.purchases_required} popsicles`}</div>
          <div className="mt-6 flex justify-center">
            <div className="w-full max-w-[640px]">
              <LoyaltyTrail progress={current?.progress || 0} target={current?.target || 6} completed={completed} variant="hero" />
            </div>
          </div>
          {completed && current?.reward_code && (
            <div className="mt-4 rounded-2xl bg-brand-yellow/15 border border-brand-yellow/25 p-4" data-testid="loyalty-reward-ready">
              <div className="text-[10px] uppercase tracking-widest text-brand-yellow">Reward Code</div>
              <div className="mt-1 font-mono text-2xl tracking-widest text-brand-cream">{current.reward_code}</div>
              <div className="mt-2 text-brand-cream/70 text-sm">Show this at any Pop Panda Go store to claim your free popsicle. Your journey resets automatically after redemption.</div>
            </div>
          )}
          <div className="mt-6 text-[11px] text-brand-cream/55">
            Each qualifying popsicle purchase advances your journey automatically when staff scans your Membership QR. {config?.eligible_notes || ""}
          </div>
        </div>
      </GlassCard>

      <GlassCard>
        <SectionHead icon={Sparkles} label="Loyalty Stats" />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <StatBox label="Lifetime earned" value={lifetime_earned} />
          <StatBox label="Lifetime redeemed" value={lifetime_redeemed} />
          <StatBox label="Cycles completed" value={history.length} />
          <StatBox label="This cycle" value={`${current?.progress || 0}/${current?.target || 6}`} />
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-brand-cream/55 mb-2">Previous Journeys</div>
          {history.length <= 1 ? (
            <div className="text-brand-cream/45 text-sm">No completed journeys yet. Your first free popsicle is only a few purchases away!</div>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-auto">
              {history.filter((c) => c.status !== "active").map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                  <div>
                    <div className="text-sm text-brand-cream">Cycle #{c.cycle_number}</div>
                    <div className="text-[11px] text-brand-cream/55">
                      {c.status === "redeemed" ? `Redeemed ${new Date(c.redeemed_at).toLocaleDateString()}` : c.status === "reset" ? "Reset by admin" : `Completed ${new Date(c.completed_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${c.status === "redeemed" ? "bg-emerald-500/20 text-emerald-300" : c.status === "completed" ? "bg-brand-yellow/20 text-brand-yellow" : "bg-white/10 text-brand-cream/60"}`}>
                    {c.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </GlassCard>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-brand-cream/55">{label}</div>
      <div className="font-display text-xl text-brand-cream mt-1">{value}</div>
    </div>
  );
}


function HistoryPane({ transactions, redemptions }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <GlassCard>
        <SectionHead icon={Coins} label="Coin Transactions" />        {(!transactions || transactions.length === 0) ? (
          <div className="mt-6 text-brand-cream/50 text-sm">No transactions yet.</div>
        ) : (
          <ul className="mt-4 space-y-3">
            {transactions.map((t) => <TxnRow key={t.id} t={t} />)}
          </ul>
        )}
      </GlassCard>
      <GlassCard>
        <SectionHead icon={Gift} label="Redemptions" />
        {(!redemptions || redemptions.length === 0) ? (
          <div className="mt-6 text-brand-cream/50 text-sm">No rewards redeemed yet.</div>
        ) : (
          <ul className="mt-4 space-y-3 text-sm">
            {redemptions.map((r) => (
              <li key={r.id} className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-brand-cream">{r.reward_title}</div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    r.status === "claimed" ? "bg-emerald-500/20 text-emerald-300" : "bg-brand-yellow/20 text-brand-yellow"
                  }`}>{r.status}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-brand-cream/60">
                  <span className="font-mono">{r.code}</span>
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </div>
  );
}

function TxnRow({ t }) {
  const isCredit = t.delta > 0;
  const mode = t.meta?.credit_mode;
  const bill = t.meta?.bill_amount ?? t.meta?.amount_inr;
  const noteText = t.meta?.note;
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
      <div className={`h-9 w-9 rounded-full grid place-items-center ${isCredit ? "bg-emerald-500/20 text-emerald-300" : "bg-[#FF4F5A]/20 text-[#FF9EA5]"}`}>
        {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-brand-cream capitalize">{t.reason.replace(/_/g, " ")}</span>
          {mode && (
            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${mode === "auto" ? "bg-brand-yellow/20 text-brand-yellow" : "bg-white/10 text-brand-cream/70"}`}>
              {mode === "auto" ? "Auto" : "Manual"}
            </span>
          )}
        </div>
        <div className="text-[11px] text-brand-cream/50 flex items-center gap-2 mt-0.5">
          <span>{new Date(t.created_at).toLocaleString()}</span>
          {bill && <span>· Bill ₹{bill}</span>}
          {noteText && <span className="truncate">· {noteText}</span>}
        </div>
      </div>
      <div className={`font-mono text-sm ${isCredit ? "text-emerald-300" : "text-[#FF9EA5]"}`}>{isCredit ? "+" : ""}{t.delta}</div>
    </li>
  );
}

function GlassCard({ children, className = "" }) {
  return <div className={`rounded-3xl bg-white/10 border border-white/15 backdrop-blur-2xl p-6 ${className}`}>{children}</div>;
}

function SectionHead({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-brand-yellow">
      <Icon size={16} /><div className="text-[11px] uppercase tracking-[0.24em]">{label}</div>
    </div>
  );
}

function Info({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-brand-cream/55 text-xs uppercase tracking-widest">{k}</dt>
      <dd className={`text-brand-cream ${mono ? "font-mono" : ""}`}>{v}</dd>
    </div>
  );
}

function RedemptionModal({ data, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md grid place-items-center p-4">
      <motion.div
        initial={{ scale: 0.85, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="rounded-3xl bg-gradient-to-br from-brand-yellow to-[#FFB300] p-8 max-w-md w-full text-brand-charcoal relative shadow-2xl"
        data-testid="redemption-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-brand-charcoal/60 hover:text-brand-charcoal" data-testid="close-redemption-modal"><X size={20} /></button>
        <div className="text-[11px] uppercase tracking-[0.28em]">Redemption Successful</div>
        <h3 className="font-display text-3xl mt-2 leading-tight">{data.reward.title}</h3>
        <div className="mt-6 rounded-2xl bg-brand-charcoal text-brand-cream p-5 text-center">
          <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Show this code at the outlet</div>
          <div className="mt-2 font-mono text-3xl tracking-[0.3em]" data-testid="redemption-code">{data.code}</div>
        </div>
        <div className="mt-4 text-sm text-brand-charcoal/75">
          Screenshot this or find it later in your Redemptions history. One-time use only.
        </div>
        <button onClick={onClose} className="mt-6 w-full rounded-full bg-brand-charcoal text-brand-cream py-3 hover:opacity-90">Awesome, thanks!</button>
      </motion.div>
    </motion.div>
  );
}

function PaymentsPane() {
  const [payments, setPayments] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await memberApi.get(`/member/payments${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setPayments(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="grid gap-4" data-testid="payments-pane">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Your payments</div>
          <div className="font-display text-2xl text-brand-cream">Every payment. Every reward.</div>
        </div>
        <div className="flex gap-2 items-center">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search receipt / outlet / txn" className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm text-brand-cream outline-none focus:border-brand-yellow" data-testid="payments-search" />
          <button onClick={load} className="rounded-full bg-brand-yellow text-brand-charcoal px-4 py-2 text-xs font-semibold" data-testid="payments-refresh">Search</button>
        </div>
      </div>

      {loading ? (
        <div className="text-brand-cream/60 text-sm">Loading…</div>
      ) : payments.length === 0 ? (
        <div className="rounded-3xl bg-white/[0.05] border border-white/12 backdrop-blur-2xl p-8 text-center">
          <div className="font-display text-xl text-brand-cream">No payments yet</div>
          <div className="text-sm text-brand-cream/60 mt-1">Visit any Pop Panda Go outlet, scan the QR at the counter and enjoy Pay & Scratch.</div>
        </div>
      ) : (
        <ul className="grid gap-3">
          {payments.map((p) => {
            const sc = p.scratch_card;
            return (
              <li key={p.id} className="rounded-2xl bg-white/[0.05] border border-white/12 backdrop-blur-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`payment-${p.id}`}>
                <div className="min-w-[200px] flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-brand-cream/50">{p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}</div>
                  <div className="font-display text-lg text-brand-cream mt-0.5">₹{Number(p.amount).toFixed(2)} · {p.outlet_name}</div>
                  <div className="text-xs text-brand-cream/60 mt-0.5">Receipt <span className="font-mono">{p.receipt_number || "—"}</span></div>
                </div>
                <div className="text-right">
                  {sc?.reward_snapshot?.is_win ? (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow border border-brand-yellow/30 px-3 py-1 text-xs">🎁 {sc.reward_snapshot.name}</div>
                  ) : sc ? (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-brand-cream/70 border border-white/15 px-3 py-1 text-xs">Better luck</div>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Link to={`/scratch/receipt/${p.id}`} className="text-xs rounded-full bg-white/10 border border-white/20 text-brand-cream px-3 py-1.5 hover:bg-white/20" data-testid={`payment-view-${p.id}`}>View receipt</Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

